import { QRCode } from "@pandas/components";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import Button from "@/components/common/Buttons/Button";
import Popup from "@/components/common/Popups/Popup";
import LoadingDots from "@/components/LoadingDots";
import { formatTimeRemaining } from "@/components/webapp-assessment/formatTimeRemaning";
import useAssessmentFlowUrl from "@/hooks/useAssessmentFlowUrl";
import useNavigationHelpers from "@/hooks/useNavigationHelpers";
import useUrlShortener from "@/hooks/useUrlShortener";
import type { PeerMessage } from "@/lib/ably/vision";
import { useLastMessageAction, useVisionChannel } from "@/lib/ably/vision";
import { publishStartFunctionalAssessments } from "@/lib/ably/vision/messaging/functionalAssessmentsRedirect";
import { assessmentEngine } from "@/lib/assessments/assessmentEngineSingleton";
import logger from "@/lib/logging/logger";
import { useStore } from "@/store";

export const Route = createFileRoute("/$locale/$partner/_app/_card/_paired/webapp-assessment")({
	component: WebappAssessment,
});

function WebappAssessment() {
	const { t } = useTranslation();
	const setTimer = useStore((state) => state.setTimer);
	const clearTimer = useStore((state) => state.clearTimer);
	const setError = useStore((state) => state.setError);
	const setPeerPhase = useStore((s) => s.setPeerPhase);
	const functionalAssessmentExecution = useStore((state) => state.config?.functionalAssessmentExecution);
	const setAssessmentEngineState = useStore((state) => state.setAssessmentEngineState);
	const timeRemaining = useStore((state) => state.timer?.timeRemaining);
	const sessionId = useStore((state) => state.sessionId);
	const initializedTimerSessionRef = useRef<string | null>(null);
	const startedSessionRef = useRef<string | null>(null);
	const completedSessionRef = useRef<string | null>(null);
	const [showModal, setShowModal] = useState(false);
	const { shortUrl, getShortUrl } = useUrlShortener();
	const { goToNextStep, isLastStep } = useNavigationHelpers();
	const assessmentFlowUrl = useAssessmentFlowUrl();

	const initTimer = useCallback(() => {
		if (isLastStep) {
			setTimer({ totalTimeout: 25, popupTimeout: 0 });
			return;
		}

		clearTimer();
	}, [clearTimer, isLastStep, setTimer]);

	const handleAblyMessage = useCallback(
		(message: PeerMessage) => {
			if (isLastStep) return;
			if (message.name === "session-status-update" && message.data.status === "functional-assessments-completed") {
				if (!sessionId || completedSessionRef.current === sessionId) return;
				completedSessionRef.current = sessionId;
				if (message.data.metadata && message.data.metadata.type === "assessment-state") {
					assessmentEngine.ImportAndAttachToState(message.data.metadata.entries, setAssessmentEngineState);
				}
				// Realtime-driven advance (never a user double-tap), and useLastMessage
				// can replay this status on mount — bypass the double-tap cooldown so it
				// is never dropped for arriving within the cooldown window.
				goToNextStep(undefined, { immediate: true });
			}
		},
		[goToNextStep, isLastStep, sessionId, setAssessmentEngineState],
	);

	const { channel: ablyChannel, connectionStatus, safePublish } = useVisionChannel(sessionId, handleAblyMessage);
	useLastMessageAction(ablyChannel, handleAblyMessage);

	const redirectToWebapp = useCallback(async (): Promise<boolean> => {
		try {
			await publishStartFunctionalAssessments(
				safePublish,
				functionalAssessmentExecution === "external" ? { type: "redirection", url: assessmentFlowUrl } : undefined,
			);
			setPeerPhase("redirection");
			return true;
		} catch (error) {
			setError(error);
			setTimer({ totalTimeout: 120, popupTimeout: 0 });
			logger.warning("FLOW", "Could not redirect to webapp", error);
			return false;
		}
	}, [safePublish, assessmentFlowUrl, functionalAssessmentExecution, setError, setPeerPhase, setTimer]);

	useEffect(() => {
		if (!sessionId) {
			initializedTimerSessionRef.current = null;
			startedSessionRef.current = null;
			completedSessionRef.current = null;
			return;
		}

		if (completedSessionRef.current && completedSessionRef.current !== sessionId) {
			completedSessionRef.current = null;
		}

		setError(null);
		if (!connectionStatus.isInitialCheckComplete) return;
		if (initializedTimerSessionRef.current !== sessionId) {
			initTimer();
			initializedTimerSessionRef.current = sessionId;
		}

		if (startedSessionRef.current === sessionId) return;
		startedSessionRef.current = sessionId;
		redirectToWebapp().then((success) => {
			if (success) {
				initTimer();
			} else if (startedSessionRef.current === sessionId) {
				startedSessionRef.current = null;
			}
		});
	}, [
		connectionStatus.isInitialCheckComplete,
		connectionStatus.isEvaluatedDeviceConnected,
		initTimer,
		redirectToWebapp,
		sessionId,
		setError,
	]);

	const displayRedirectUrlModal = () => {
		getShortUrl(assessmentFlowUrl);
		setTimer({ totalTimeout: 120, popupTimeout: 0 });
		setShowModal(true);
	};

	const hideRedirectUrlModal = (value: boolean) => {
		initTimer();
		setShowModal(value);
	};

	return (
		<>
			<div className="flex flex-col justify-between items-center w-full h-full pb-4">
				<div className="pt-10 lg:pt-20 text-center gap-4 flex flex-col">
					<h3>{t("instructions.Continue on your device")}</h3>
					{timeRemaining !== undefined && (
						<p className="font-bold">
							<span>{t("instructions.This screen will close automatically in")}</span>&nbsp;
							<span>{formatTimeRemaining(timeRemaining)}</span>
						</p>
					)}
				</div>
				<div className="w-full h-72 sm:h-[500px] max-h-[40vh] relative">
					<picture>
						{/* Mobile-specific image (up to 639px wide) */}
						<source
							srcSet="/images/continue-on-your-device-mobile.svg"
							media="(max-width: 639px)"
						/>

						{/* Default / larger breakpoints (sm and up) */}
						<img
							src="/images/continue-on-your-device.svg"
							alt="device"
							className="w-full h-full object-contain"
						/>
					</picture>
				</div>

				<Button
					intent="quaternary"
					size="small"
					onClick={displayRedirectUrlModal}
					label={
						<Trans i18nKey={"btn.Having Trouble? Tap here  to open the web app on your device."}>
							<div></div>
							<u></u>
							<span></span>
						</Trans>
					}
				/>
			</div>
			<Popup
				show={showModal}
				setShow={hideRedirectUrlModal}
				content={
					<div className="flex flex-col justify-center items-center h-full">
						<h4 className="font-semibold">
							{t("popup.Scan the QR with the evaluated device to start functional test")}
						</h4>
						<QRCode
							id="qr-code"
							size={100}
							className="bg-white rounded-lg p-6 sm:p-8 mx-auto w-[180px] h-[180px] sm:w-[300px] sm:h-[300px]"
							value={assessmentFlowUrl}
						/>
						<div className="bg-lightgrey p-4 rounded-lg">
							<h6 className="my-2">{t("popup.You can also enter the URL manually on the evaluated device.")}</h6>
							<h4 className="font-semibold">{shortUrl ? shortUrl : <LoadingDots />}</h4>
						</div>
					</div>
				}
			/>
		</>
	);
}
